import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Folders, ListChecks, Ticket, TrendUp, ChartBar, Users, CurrencyDollar, Target, Buildings
} from "@phosphor-icons/react";

const Stat = ({ label, value, sub, color = "#0055FF", icon: Icon }) => (
  <div className="flex items-center gap-3">
    {Icon && <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + "10" }}><Icon size={20} style={{ color }} weight="duotone" /></div>}
    <div>
      <p className="text-2xl font-black text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value ?? 0}</p>
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">{label}</p>
      {sub && <p className="text-[10px] text-[#525252] mt-0.5">{sub}</p>}
    </div>
  </div>
);

const BarSegment = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex-1">
      <div className="h-6 relative" style={{ backgroundColor: "#F5F5F5" }}>
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <p className="text-[10px] text-center mt-1 font-bold text-[#525252]">{value}</p>
      <p className="text-[9px] text-center text-[#A3A3A3] uppercase tracking-wider">{label}</p>
    </div>
  );
};

/**
 * Insights content — shown as a tab inside DashboardPage.
 * Deliberately excludes any KPI that already appears on the Dashboard tab
 * (e.g. "Active Projects", "Open Tickets", "Total Proposals", the Sales
 * Pipeline stage widget, etc.). Focuses on breakdowns + aggregates that
 * aren't surfaced there.
 */
export function InsightsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/insights/stats")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || "Unable to load insights"); setLoading(false); });
  }, []);

  if (loading) return <div className="p-8 flex justify-center"><div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;
  if (error || !data) return (
    <div className="p-8 text-center text-[#A3A3A3]" data-testid="insights-empty">
      <ChartBar size={32} className="mx-auto mb-3 text-[#E5E5E5]" />
      <p className="text-sm">{error || "Unable to load insights"}</p>
    </div>
  );

  const visible = new Set(data.visible_modules || []);
  const t  = data.tasks || {};
  const tk = data.tickets || {};
  const pr = data.proposals || {};
  const c  = data.campaigns || {};
  const cl = data.clients || {};

  const taskTotal = t.total || 0;
  const taskDone = t.done || 0;
  const taskPct = taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0;

  const revenueByStatus = data.revenue_by_status || [];
  const totalRevenue = revenueByStatus.reduce((s, r) => s + (r.total_value || 0), 0);

  const campaignsTotal = (c.active || 0) + (c.inactive || 0);

  return (
    <div className="space-y-8" data-testid="insights-tab">
      {/* Projects — breakdowns only (totals are on Dashboard) */}
      {visible.has("projects") && (
      <div data-testid="insights-section-projects">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3 flex items-center gap-1.5"><Folders size={14} /> Project Delivery</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Tasks Completed" value={taskDone} sub={`${taskPct}% of ${taskTotal}`} color="#16A34A" icon={Target} /></Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Tasks Overdue" value={t.overdue} color="#DC2626" icon={TrendUp} /></Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="In Review" value={t.review} color="#7C3AED" icon={ListChecks} /></Card>
        </div>

        <Card className="border border-[#E5E5E5] rounded-none p-4 mt-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3">Task Status Breakdown</p>
          <div className="flex gap-1">
            <BarSegment label="To Do" value={t.todo || 0} total={taskTotal} color="#0055FF" />
            <BarSegment label="In Progress" value={t.in_progress || 0} total={taskTotal} color="#D97706" />
            <BarSegment label="Review" value={t.review || 0} total={taskTotal} color="#7C3AED" />
            <BarSegment label="Done" value={taskDone} total={taskTotal} color="#16A34A" />
          </div>
        </Card>

        {(data.project_completion || []).length > 0 && (
          <Card className="border border-[#E5E5E5] rounded-none p-4 mt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3">Per-Project Completion</p>
            <div className="space-y-3">
              {data.project_completion.map((pc, i) => {
                const pct = pc.total_tasks > 0 ? Math.round((pc.done_tasks / pc.total_tasks) * 100) : 0;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-[#0A0A0A]">{pc.name}</span>
                      <span className="text-[10px] text-[#A3A3A3]">{pc.done_tasks}/{pc.total_tasks} tasks ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-[#F5F5F5]"><div className="h-full bg-[#0055FF] transition-all" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
      )}

      {/* Support — breakdowns (totals + open are on Dashboard) */}
      {visible.has("support") && (
      <div data-testid="insights-section-support">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3 flex items-center gap-1.5"><Ticket size={14} /> Support Performance</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="In Progress" value={tk.in_progress} color="#D97706" icon={Ticket} /></Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="SLA Breached" value={(tk.sla_response_breached || 0) + (tk.sla_resolution_breached || 0)} color="#DC2626" icon={TrendUp} /></Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Avg Resolution" value={data.avg_resolution_hours != null ? `${data.avg_resolution_hours}h` : "N/A"} color="#525252" icon={ChartBar} /></Card>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <Card className="border border-[#E5E5E5] rounded-none p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3">By Priority</p>
            {(data.tickets_by_priority || []).length > 0 ? (
              <div className="space-y-2">
                {data.tickets_by_priority.map((tp, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <Badge className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${tp.priority === "critical" ? "bg-[#FEF2F2] text-[#DC2626]" : tp.priority === "high" ? "bg-[#FFF7ED] text-[#EA580C]" : tp.priority === "medium" ? "bg-[#FFFBEB] text-[#D97706]" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}>{tp.priority}</Badge>
                    <span className="text-sm font-bold text-[#0A0A0A]">{tp.count}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-[#A3A3A3] text-center py-4">No tickets yet</p>}
          </Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3">Source</p>
            <div className="flex items-center gap-6">
              <div><p className="text-xl font-black text-[#0A0A0A]">{tk.via_email || 0}</p><p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Email</p></div>
              <div><p className="text-xl font-black text-[#0A0A0A]">{tk.via_portal || 0}</p><p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Portal</p></div>
            </div>
          </Card>
        </div>
      </div>
      )}

      {/* Sales — revenue only (headline counts are on Dashboard) */}
      {visible.has("sales") && (
      <div data-testid="insights-section-sales">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3 flex items-center gap-1.5"><CurrencyDollar size={14} /> Revenue</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Pipeline Value" value={`$${totalRevenue.toLocaleString()}`} color="#0055FF" icon={CurrencyDollar} /></Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Rejected" value={pr.rejected} color="#DC2626" icon={TrendUp} /></Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Expired" value={pr.expired} color="#A3A3A3" icon={TrendUp} /></Card>
        </div>
        {revenueByStatus.length > 0 && (
          <Card className="border border-[#E5E5E5] rounded-none p-4 mt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3">Revenue by Proposal Status</p>
            <div className="space-y-2">
              {revenueByStatus.map((r, i) => (
                <div key={i} className="flex items-center justify-between">
                  <Badge className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-none border-0 bg-[#F5F5F5] text-[#525252]">{r.status}</Badge>
                  <span className="text-sm font-bold text-[#0A0A0A]">${(r.total_value || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      )}

      {/* Campaigns — Active vs Inactive split (Dashboard shows active-count only) */}
      {visible.has("campaigns") && (
      <div data-testid="insights-section-campaigns">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3 flex items-center gap-1.5"><Users size={14} /> Campaign Mix</h2>
        <Card className="border border-[#E5E5E5] rounded-none p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3">Active vs Inactive ({campaignsTotal} total)</p>
          <div className="flex gap-1">
            <BarSegment label="Active" value={c.active || 0} total={Math.max(campaignsTotal, 1)} color="#16A34A" />
            <BarSegment label="Inactive" value={c.inactive || 0} total={Math.max(campaignsTotal, 1)} color="#A3A3A3" />
          </div>
        </Card>
      </div>
      )}

      {/* Clients directory — not surfaced on Dashboard at all */}
      {visible.has("clients") && (
      <div data-testid="insights-section-clients">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3 flex items-center gap-1.5"><Buildings size={14} /> Client Directory</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Total Clients" value={cl.total} icon={Buildings} /></Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Active" value={cl.active} color="#16A34A" icon={Buildings} /></Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Inactive" value={cl.inactive} color="#A3A3A3" icon={Buildings} /></Card>
          <Card className="border border-[#E5E5E5] rounded-none p-4"><Stat label="Archived" value={cl.archived} color="#525252" icon={Buildings} /></Card>
        </div>
      </div>
      )}
    </div>
  );
}

// Legacy /insights route → merged into /dashboard as a tab.
export default function InsightsPage() {
  return <Navigate to="/dashboard?tab=insights" replace />;
}
